import React from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { attachRouter } from 'appHistory';
import Guard from 'components/PrivateRoute/index';
import Shell from './Shell';
import ForgotPassword from 'pages/Auth/ForgotPassword';
import LoginPage from 'pages/Auth/LoginPage';
import Logout from 'pages/Auth/Logout';
import PasswordRecovery from 'pages/Auth/PasswordRecovery';
import AccountManagement from 'pages/AppBarMenu/AccountManagement';
import Conditions from 'pages/CoreEntities/Conditions';
import Descriptors from 'pages/CoreEntities/Descriptors';
import EchaStatus from 'pages/EchaStatus';
import Echas from 'pages/Echas';
import EvaluationProduct from 'pages/Evaluation/Product';
import ExposureExternal from 'pages/ScenarioExposure/Product';
import ExposureInternal from 'pages/ScenarioExposure/Flow';
import FlowProducts from 'pages/Products/FlowProducts';
import Flows from 'pages/Flows/Flows';
import FlowsScenarios from 'pages/Flows/FlowScenarios';
import FlowsSteps from 'pages/Flows/FlowsSteps';
import ImportData from 'pages/import/ImportData';
import ImportEchas from 'pages/import/ImportEchas';
import Manuals from 'pages/AppBarMenu/Manuals';
import Mixtures from 'pages/Inventory/Mixtures';
import ProductRM from 'pages/Products/ProductRM';
import ProductScenarios from 'pages/Products/ProductScenarios';
import Products from 'pages/Products/Products';
import RawMaterialConsumptions from 'pages/Inventory/Consumptions';
import RawMaterials from 'pages/Inventory/RawMaterials';
import RawMaterialsSuppliers from 'pages/Inventory/Shipments';
import ReachOKAccountManagement from 'pages/AppBarMenu/ReachOKAccountManagement';
import Scenarios from 'pages/CoreEntities/Scenarios';
import SubstancesPolimers from 'pages/Inventory/substancesPolimers';
import Suppliers from 'pages/Inventory/Suppliers';
import TypeDescriptors from 'pages/CoreEntities/TypeDescriptors';
import Units from 'pages/CoreEntities/Units';
import UserData from 'pages/AppBarMenu/UserData';
import UsersManagement from 'pages/AppBarMenu/UsersManagement';

/*
 * The route table, as data.
 *
 * Router 5 read the routes out of the rendered tree, so they lived in JSX inside
 * `App` and `Routes.js`. A data router wants them before anything renders, which
 * is what makes `router.navigate` possible from outside React at all - see
 * history.js. Same thirty-one paths, same components, same titles; the titles
 * ride in `handle` because the guard is one layout route now and reads them back
 * with `useMatches`.
 *
 * The four public routes stay siblings of the shell rather than children of it:
 * /login must not draw the header, and must not be behind the guard that would
 * send it to /login.
 */
const router = createBrowserRouter([
  { path: '/login', element: <LoginPage/> },
  { path: '/forgot_password', element: <ForgotPassword/> },
  { path: '/password_recovery/:code', element: <PasswordRecovery/> },
  { path: '/logout', element: <Logout/> },
  {
    path: '/',
    element: <Shell/>,
    children: [{
      element: <Guard/>,
      children: [
      { path: 'suppliers', element: <Suppliers/>, handle: { title: 'FORNECEDORES' } },
      { path: 'raw-materials', element: <RawMaterials/>, handle: { title: 'MATÉRIAS PRIMAS' } },
      { path: 'raw-materials-suppliers', element: <RawMaterialsSuppliers/>, handle: { title: 'FORNECIMENTOS' } },
      { path: 'raw-material-consumptions', element: <RawMaterialConsumptions/>, handle: { title: 'CONSUMOS' } },
      { path: 'mixtures', element: <Mixtures/>, handle: { title: 'MISTURAS' } },
      { path: 'substance-polimers', element: <SubstancesPolimers/>, handle: { title: 'SUBSTÂNCIAS E POLÍMEROS' } },
      { path: 'clients', element: <ReachOKAccountManagement/> },
      { path: 'manuals', element: <Manuals/> },
      { path: 'userdata', element: <UserData/> },
      { path: 'usersmanagement', element: <UsersManagement/> },
      { path: 'accountmanagement', element: <AccountManagement/> },
      { path: 'flow-steps', element: <Flows/>, handle: { title: 'FLUXOGRAMAS' } },
      { path: 'flow-steps-rm', element: <FlowsSteps/>, handle: { title: 'ETAPAS - MATÉRIAS PRIMAS' } },
      { path: 'flow-scenarios', element: <FlowsScenarios/>, handle: { title: 'CENÁRIOS PRODUÇÃO' } },
      { path: 'products', element: <Products/>, handle: { title: 'PRODUTOS' } },
      { path: 'product-flows', element: <FlowProducts/>, handle: { title: 'PRODUTOS - FLUXOGRAMA' } },
      { path: 'product-rms', element: <ProductRM/>, handle: { title: 'COMPOSIÇÃO PRODUTO' } },
      { path: 'product-scenarios', element: <ProductScenarios/>, handle: { title: 'CENÁRIOS PRODUTO' } },
      { path: 'exposure-internal', element: <ExposureInternal/>, handle: { title: 'CE DE MP - NA PRODUÇÃO' } },
      { path: 'exposure-external', element: <ExposureExternal/>, handle: { title: 'CE DE MP - NO PRODUTO' } },
      { path: 'evaluation-production', element: <EvaluationProduct/>, handle: { title: 'AVALIAÇÃO PRODUÇÃO' } },
      { path: 'evaluation-product', element: <EvaluationProduct/>, handle: { title: 'AVALIAÇÃO PRODUTO' } },
      { path: 'echa-status', element: <EchaStatus/>, handle: { title: 'LIGAÇÃO MATÉRIAS PRIMAS - ECHA' } },
      { path: 'units', element: <Units/>, handle: { title: 'UNIDADES' } },
      { path: 'descriptors', element: <Descriptors/>, handle: { title: 'DESCRITORES' } },
      { path: 'type-descriptors', element: <TypeDescriptors/>, handle: { title: 'TIPOS DE DESCRITORES' } },
      { path: 'conditions', element: <Conditions/>, handle: { title: 'CONDIÇÕES' } },
      { path: 'echas', element: <Echas/>, handle: { title: 'LISTA DE ECHAs' } },
      { path: 'scenarios', element: <Scenarios/>, handle: { title: 'LISTA TOTAL DE CENARIOS' } },
      { path: 'import-echas', element: <ImportEchas/>, handle: { title: 'IMPORTAÇÃO DE ECHAs' } },
      { path: 'import-data', element: <ImportData/>, handle: { title: 'IMPORTAÇÃO DE DADOS' } },
        // `<Redirect to='/raw-materials'/>` was the last child of the old
        // Switch: anything that matched nothing landed there - INCLUDING `/`
        // itself, because a `<Redirect>` with no path matched everything the
        // routes above had not claimed.
        //
        // A splat does not. `*` matches a remaining pathname, and at `/` there
        // is nothing remaining: the match stops at the shell, whose `<Outlet/>`
        // then has no child to draw, and the screen is the header over an empty
        // page. That is where `history.push('/')` sends every person who logs
        // in - see `ducks/auth.js` - so it was the first thing anyone saw.
        //
        // The index route is the half a splat cannot cover: it matches the
        // parent's own path and nothing else. The two together are what the one
        // `<Redirect>` used to be.
        { index: true, element: <Navigate to='/raw-materials' replace/> },
        { path: '*', element: <Navigate to='/raw-materials' replace/> },
      ],
    }],
  },
]);

attachRouter(router);

export default router;
